import type { ValidFormField } from "src/components/Form/Form.types.ts";
import type { HiddenField, HiddenFields } from "src/system/module.types.ts";
import type { UseFormGetValues } from "react-hook-form";
import { unique } from "@exonn/utils";
import { getAccessedPropertyNames } from "src/components/Form/utils/getAccessedPropertyNames.ts";
import { getFieldConfig } from "src/components/Form/utils/getFieldConfig.ts";

export const getNamesToWatchForHiddenFields = ({
  names,
  fields,
  hidden_fields,
  getValues,
}: {
  names: string[];
  fields: Record<string, ValidFormField<any>>;
  hidden_fields: HiddenFields<any> | undefined;
  getValues: UseFormGetValues<any>;
}): string[] => {
  let namesToWatch: string[] = [];
  names.forEach((name) => {
    const hiddenFieldConfig = getHiddenFieldConfig({
      fields,
      hidden_fields,
      name,
    });
    namesToWatch = [
      ...namesToWatch,
      ...(hiddenFieldConfig
        ? getHiddenFieldNames({
            hiddenField: hiddenFieldConfig,
            values: getValues(),
          })
        : []),
    ];
  });

  return unique(namesToWatch);
};

export const getHiddenFieldConfig = ({
  hidden_fields,
  fields,
  name,
}: {
  fields: Record<string, ValidFormField<any>>;
  hidden_fields: HiddenFields<any> | undefined;
  name: string;
}) => {
  const field = getFieldConfig(fields, name);

  const hiddenFieldV1 = hidden_fields?.[name];
  const hiddenFieldV2 = field?.hidden;
  const hiddenField = hiddenFieldV2 || hiddenFieldV1;
  if (hiddenFieldV1 && hiddenFieldV2) {
    throw new Error(
      "you are mixing different approaches for hiding fields." +
        " Use either the old version e.g. 'disabled_fields'" +
        " or use the new version e.g. 'hidden' prop on a field",
    );
  }
  return hiddenField;
};

export const getHiddenFieldNames = ({
  hiddenField,
  values,
}: {
  hiddenField: HiddenField<any>;
  values: any;
}): string[] => {
  if (typeof hiddenField === "function") {
    return getAccessedPropertyNames({
      callback: hiddenField,
      object: values,
    });
  }
  if (hiddenField.watch_names) {
    return hiddenField.watch_names as string[];
  }
  return getAccessedPropertyNames({
    callback: hiddenField.hide_condition,
    object: values,
  });
};
